import { useCallback, useEffect, useState } from "react"

import { ENABLE_ADMIN_CSS_VARIABLE_EDITOR } from "@/config/features"
import {
  applyCategoryColorOverrides,
  initializeCssVariables,
} from "@/hooks/use-css-variables"

export type Appearance = "light" | "dark" | "system"

const DEFAULT_APPEARANCE: Appearance = "light"

const prefersDark = () => {
  if (typeof window === "undefined") {
    return false
  }
  return window.matchMedia("(prefers-color-scheme: dark)").matches
}

const applyTheme = (appearance: Appearance) => {
  const isDark =
    appearance === "dark" || (appearance === "system" && prefersDark())

  document.documentElement.classList.toggle("dark", isDark)
  document.documentElement.style.colorScheme = isDark ? "dark" : "light"

  // Re-apply category color overrides when theme changes
  if (ENABLE_ADMIN_CSS_VARIABLE_EDITOR) {
    applyCategoryColorOverrides()
  }
}

const mediaQuery = () => {
  if (typeof window === "undefined") {
    return null
  }

  return window.matchMedia("(prefers-color-scheme: dark)")
}

const getStoredAppearance = () =>
  (localStorage.getItem("appearance") as Appearance | null) ??
  DEFAULT_APPEARANCE

const handleSystemThemeChange = () => {
  applyTheme(getStoredAppearance())
}

export function initializeTheme() {
  applyTheme(getStoredAppearance())

  // Apply any custom CSS variables from localStorage
  if (ENABLE_ADMIN_CSS_VARIABLE_EDITOR) {
    initializeCssVariables()

    // Apply category color overrides from localStorage
    applyCategoryColorOverrides()
  }

  mediaQuery()?.addEventListener("change", handleSystemThemeChange)
}

export function useAppearance() {
  const [appearance, setAppearance] = useState<Appearance>(() => {
    if (typeof window === "undefined") return DEFAULT_APPEARANCE
    return getStoredAppearance()
  })

  const updateAppearance = useCallback((mode: Appearance) => {
    setAppearance(mode)
    localStorage.setItem("appearance", mode)
    applyTheme(mode)
  }, [])

  useEffect(() => {
    applyTheme(appearance)

    return () =>
      mediaQuery()?.removeEventListener("change", handleSystemThemeChange)
  }, [appearance])

  return { appearance, updateAppearance } as const
}
